import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../services/Api/Admin/adminApi";
import { partnerApi } from "../../../../services/Api/Partner/partnerApi";
import { LoanApi, OrganizationApi } from "../../../../services/Api/types";
import { selectPartner } from "../../../../services/partner/selectors";
import { UserState } from "../../../../services/users/types";
import LoanAttachments from "./LoanAttachments";
import LoanItemsTable from "./LoanItemsTable";

interface Props {
	loan: LoanApi;
	handleUpdate: (data: LoanApi) => void;
	provider?: string;
	organization?: OrganizationApi;
	user: UserState;
}

const LoanForm = ({ loan, user, organization }: Props) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation("admin");
	const history = useHistory();
	const partner = useSelector(selectPartner);
	const isPosnet = user?.roles?.includes("POSNET");
	const isAdmin = user.admin;
	const { addFlash, addSuccessFlash } = useFlash();
	const defaultOrg = organization ? organization : loan.organization;
	const form = useForm<LoanApi>({
		defaultValues: {
			...loan,
			organization: loan.organization,
			organization_id: !loan.id && defaultOrg ? defaultOrg.id : loan.organization.id,
		},
		criteriaMode: "all",
	});
	const {
		register,
		control,
		handleSubmit,
		formState,
		formState: { errors },
		setError,
		reset,
	} = form;

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		const params = { include: "attachments,items,organization" };
		if (!loan.id) {
			try {
				data.partner_id = partner?.id;
				if (loan.organization_id) {
					data.organization_id = loan.organization_id;
				}
				if (loan.reference_id) {
					data.reference_id = loan.reference_id;
				}
				if (loan.items && loan.items.length > 0) {
					data.items = loan.items;
				}
				data.type = loan.type;
				let resp: LoanApi;
				if (partner?.id) {
					resp = await partnerApi.createLoan(Number(partner.id), data);
				} else {
					resp = await adminApi.createLoan(data);
				}
				reset(resp);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				history.push(`/partnership/${partner?.id}/loans/${resp.id}`);
			} catch (e) {
				handleError.form(e, setError, addFlash);
			}
			setLoading(false);
		} else {
			data.id = loan.id;
			try {
				let resp: LoanApi;
				if (partner?.id) {
					resp = await partnerApi.updateLoan(Number(partner.id), data, params);
				} else resp = await adminApi.updateLoan(data, params);

				reset({ ...resp, organization_id: resp.organization.id });
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			} catch (e) {
				handleError.form(e, setError, addFlash);
			}
			setLoading(false);
		}
	});

	const licenseOptions = [
		{
			label: "Basic",
			value: "BASIC",
		},
		{
			label: "Pro",
			value: "PRO",
		},
	];

	const searchOrganizations = async (search: string) => {
		if (partner?.id) {
			const res = await partnerApi.getPartnerOrganizations(Number(partner?.id), { search });
			return res.map((data) => {
				return {
					name: data.name,
					id: Number(data.id),
				};
			});
		}

		return adminApi.getAdminOrganizations({ search }).then((res) => {
			return res.map((data: any) => {
				return {
					name: data.name,
					id: Number(data.id),
				};
			});
		});
	};

	return (
		<FormDirty
			formState={formState}
			onSubmit={onSubmit}
			loading={loading}
			buttonSubmitCustom={
				<ButtonLoading loading={loading} type={"submit"} onClick={onSubmit}>
					{loan.id ? t("common.action.save", { ns: "lib" }) : t("common.action.create_loan")}{" "}
				</ButtonLoading>
			}
		>
			<FormInput errors={errors} type={"hidden"} register={register} name={"id"} defaultValue={loan.id} />
			<FormSelectGroup
				control={control}
				errors={errors}
				name="organization_id"
				defaultValue={defaultOrg}
				disabled={loan.status !== "DRAFT"}
				getOptionValue={(opt) => opt.id}
				getOptionLabel={(opt) => opt.name}
				label={t("lib:common.word.location")}
				loadOptions={searchOrganizations}
				data-testid="organization_id"
			/>
			{!loan.id && (
				<FormInput
					errors={errors}
					label={t("common.word.reference_id")}
					name={`reference_id`}
					register={register}
				/>
			)}
			<FormSelectGroup
				errors={errors}
				name="license_level"
				options={licenseOptions}
				disabled={loan.acceptation_status !== "OPEN"}
				defaultValue={loan.license_level}
				control={control}
				label={t("modules.loan.field.loan_type.title")}
				data-testid="license_level"
			/>
			<FormDatePicker
				label={t("modules.loan.field.start_at.title")}
				errors={errors}
				name="start_at"
				control={control}
				timePicker={false}
				disabled={!(isPosnet || isAdmin)}
			/>
			<LoanItemsTable form={form} loan={loan} />
			{loan.id && (
				<fieldset className={"form-group"}>
					<LoanAttachments
						loanStatus={loan.status}
						form={form}
						loanAcceptationStatus={loan.acceptation_status}
					/>
				</fieldset>
			)}
		</FormDirty>
	);
};

export default LoanForm;
